import { useEffect, useState, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import type { DirectiveSubscribeFn, ServerDirective } from '@/lib/directives';
import { logger } from '@/lib/utils/logger';
import { APP_ROUTES } from '@/config/routes';

// Re-export so call sites importing the prop type from the component
// continue to work; the canonical definition lives in @/lib/directives.
export type { DirectiveSubscribeFn };

export interface DirectiveHandlerProps {
  subscribe: DirectiveSubscribeFn;
}

/**
 * Renders modal UI in response to typed ServerDirectives from a subscribed
 * source. Phase 1 handles three kinds end-to-end (session_expired /
 * session_dead, permission_denied / org_mismatch / post_refresh_401,
 * tier_gated) and stubs the rest with a logger.warn + null render. The
 * exhaustive switch ensures a future DirectiveKind addition surfaces as a
 * TypeScript error at this site.
 *
 * The component is exported but NOT mounted anywhere in Phase 1 — wiring
 * to SessionManagers + ApiClients happens in Phase 2.
 */
export function DirectiveHandler({ subscribe }: DirectiveHandlerProps) {
  const [directive, setDirective] = useState<ServerDirective | null>(null);
  const location = useLocation();

  useEffect(() => subscribe(setDirective), [subscribe]);

  const handleSessionExpired = useCallback(() => {
    if (directive?.redirectTo) {
      window.location.href = directive.redirectTo;
    }
  }, [directive]);

  const handleDismiss = useCallback(() => {
    setDirective(null);
  }, []);

  const handleCta = useCallback(() => {
    if (directive?.ctaUrl) {
      window.location.href = directive.ctaUrl;
    }
  }, [directive]);

  if (!directive) return null;

  // Subscription-expired page: suppress permission_denied modals so the
  // renewal flow isn't buried under a cascade of "Access Denied" overlays.
  if (
    directive.kind === 'permission_denied' &&
    location.pathname === APP_ROUTES.SUBSCRIPTION_EXPIRED
  ) {
    return null;
  }

  switch (directive.kind) {
    case 'session_expired':
    case 'session_dead':
      return (
        <div role="dialog" aria-modal="true" aria-labelledby="session-expired-title">
          <h2 id="session-expired-title">Session Expired</h2>
          <p>{directive.message}</p>
          <button onClick={handleSessionExpired}>Log In</button>
        </div>
      );

    case 'org_mismatch': {
      // The active session's org context is no longer valid — the user lost
      // membership to the token's org (backend error_type no_org_context /
      // invalid_org). Route into the fallback ladder (the org chooser) just
      // like org_disabled, rather than dead-ending on an "Access Denied" modal
      // with only a Dismiss action. The chooser handles the multi-org pick and
      // the no-usable-org UX.
      const target = directive.redirectTo ?? APP_ROUTES.ORGANIZATIONS;
      window.location.href = target;
      return null;
    }

    case 'permission_denied':
    case 'post_refresh_401':
      return (
        <div role="dialog" aria-labelledby="access-denied-title">
          <h2 id="access-denied-title">Access Denied</h2>
          <p>{directive.message}</p>
          <button onClick={handleDismiss}>Dismiss</button>
        </div>
      );

    case 'managed_access_unavailable':
      // A managed (child) org cut off from its manager. Surface the backend
      // message as-is — crucially NOT a "go subscribe" prompt, since a managed
      // org can't self-subscribe (CU-868jyv4fn).
      return (
        <div role="dialog" aria-labelledby="managed-access-title">
          <h2 id="managed-access-title">Access Unavailable</h2>
          <p>{directive.message}</p>
          <button onClick={handleDismiss}>Dismiss</button>
        </div>
      );

    case 'tier_gated':
      // aria-modal matches session_expired: the upgrade prompt is an
      // overlay action the user must address (Upgrade or Cancel) before
      // resuming flow. The Access Denied modal is intentionally
      // non-modal because Dismiss is a no-op that returns the user to
      // the prior surface.
      return (
        <div role="dialog" aria-modal="true" aria-labelledby="upgrade-required-title">
          <h2 id="upgrade-required-title">Upgrade Required</h2>
          <p>{directive.message}</p>
          <button onClick={handleCta}>{directive.ctaText ?? 'Upgrade'}</button>
          <button onClick={handleDismiss}>Cancel</button>
        </div>
      );

    case 'session_anonymous':
      // Silent transition; no UI.
      return null;

    case 'org_disabled': {
      // Backend signaled the user's current org context is unusable. Send
      // them to the org chooser (or to directive.redirectTo if specified);
      // the chooser handles the "no usable org" UX.
      const target = directive.redirectTo ?? APP_ROUTES.ORGANIZATIONS;
      window.location.href = target;
      return null;
    }

    case 'subscription_expired':
    case 'quota_exceeded':
    case 'mfa_required':
    case 'mfa_setup_required':
    case 'email_verification_required':
    case 'setup_required':
    case 'org_selection_required':
    case 'terms_acceptance_required':
      // Phase 1 stub — full per-kind handlers land in Phase 2 alongside
      // the wiring to SessionManagers + ApiClients.
      logger.warn('DirectiveHandler: kind not yet handled in Phase 1', {
        kind: directive.kind,
      });
      return null;
  }
}
